<template>
  <div class="bg-white dark:bg-[#0a0e19] py-[60px] md:py-[80px] lg:py-[135px]">
    <div
      class="mx-auto px-[12.5px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1255px]"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-[25px] items-center">
        <div
          class="xl:ltr:-mr-[25px] xl:rtl:-ml-[25px] 2xl:ltr:-mr-[45px] 2xl:rtl:-ml-[45px] rounded-[25px] order-2 lg:order-1"
        >
          <img
            src="@/assets/images/sign-in.jpg"
            alt="lock-screen-image"
            class="rounded-[25px]"
          />
        </div>
        <div
          class="xl:ltr:pl-[90px] xl:rtl:pr-[90px] 2xl:ltr:pl-[120px] 2xl:rtl:pr-[120px] order-1 lg:order-2"
        >
          <img
            src="@/assets/images/logos/sidebarlogo.png"
            alt="logo"
            class="h-10"
          />
          <div class="my-[17px] md:my-[25px]">
            <h1
              class="font-semibold text-[22px] md:text-xl lg:text-2xl mb-[5px] md:mb-[10px]"
            >
              Welcome back to Trezo!
            </h1>
            <p
              class="font-medium leading-[1.5] lg:text-md text-[#445164] dark:text-gray-400"
            >
              Enter your password to access the app again.
            </p>
          </div>
          <div class="flex items-center mb-[20px]">
            <img
              :src="userProfilePicture"
              alt="user-image"
              class="rounded-full w-[50px] border-[2px] ltr:mr-[13px] rtl:ml-[13px] border-primary-200"
            />
            <span class="font-semibold text-black dark:text-white block">
              {{ userFullName }}
            </span>
          </div>
          <form @submit.prevent="handleUnlock">
            <div class="mb-[15px] relative" id="passwordHideShow">
              <label
                class="mb-[10px] md:mb-[12px] text-black dark:text-white font-medium block"
              >
                Password
              </label>
              <input
                v-model="password"
                :type="passwordVisible ? 'text' : 'password'"
                class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
                id="password"
                placeholder="Type password"
                required
              />
              <button
                class="absolute text-lg ltr:right-[20px] rtl:left-[20px] bottom-[12px] transition-all hover:text-primary-500"
                id="toggleButton"
                type="button"
                @click="togglePasswordVisibility"
              >
                <i :class="passwordVisible ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
              </button>
            </div>
            <div v-if="errorMessage" class="text-red-500 text-sm mb-3">
              {{ errorMessage }}
            </div>
            <button
              type="submit"
              class="md:text-md block w-full text-center transition-all rounded-md font-medium mt-[20px] py-[12px] px-[25px] text-white bg-primary-500 hover:bg-primary-400"
              :disabled="loading"
            >
              <span class="flex items-center justify-center gap-[5px]">
                <i class="material-symbols-outlined" v-if="loading"> progress_activity </i>
                <i class="material-symbols-outlined" v-else> autorenew </i>
                {{ loading ? 'Unlocking...' : 'Sign In' }}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeMount, onBeforeUnmount } from "vue";
import { useRouter } from 'vue-router';
import { supabase } from "@/lib/supabaseClient";
import defaultProfileImage from "@/assets/images/admin.png";

// Define interface for user profile data
interface UserProfile {
  display_name: string;
  profile_picture_link: string;
  user_id: string;
}

export default defineComponent({
  name: "LockScreen",
  setup() {
    const router = useRouter();
    const userFullName = ref('User');
    const userProfilePicture = ref(defaultProfileImage);
    const password = ref('');
    const passwordVisible = ref(false);
    const loading = ref(false);
    const errorMessage = ref('');
    const redirectPath = ref('/dashboard');
    
    // Function to toggle password visibility
    const togglePasswordVisibility = () => {
      passwordVisible.value = !passwordVisible.value;
    };
    
    // Prevent back navigation when screen is locked
    const preventBackNavigation = () => {
      // Set flag in sessionStorage to indicate app is locked
      sessionStorage.setItem('app_locked', 'true');
      
      // Replace current history state to prevent going back
      window.history.pushState(null, '', window.location.href);
      
      // Listen for popstate (back button) events
      window.addEventListener('popstate', handlePopState);
    };
    
    // Handler for popstate events (back button)
    const handlePopState = (event: PopStateEvent) => {
      // Push another state to effectively cancel the back action
      window.history.pushState(null, '', window.location.href);
      
      // Show message about lock screen
      errorMessage.value = 'App is locked. Please enter your password to continue.';
    };
    
    // Check if user is authenticated, otherwise redirect to login
    onBeforeMount(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        router.push('/authentication/sign-in');
      } else {
        // Get previous path from localStorage if available
        const previousPath = localStorage.getItem('previousPath');
        if (previousPath) {
          redirectPath.value = previousPath;
        }
        
        // Activate back navigation prevention
        preventBackNavigation();
      }
    });
    
    // Load user profile
    onMounted(async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && session.user) {
          // Get user email from auth
          const email = session.user.email || '';
          
          // Get user profile data
          const { data, error } = await supabase
            .from('user_profiles')
            .select('display_name, profile_picture_link')
            .eq('user_id', session.user.id)
            .single();
          
          if (error) {
            // Fall back to email
            userFullName.value = email;
          } else if (data) {
            // Use profile data - add type assertion to handle the data correctly
            const profileData = data as UserProfile;
            userFullName.value = profileData.display_name || email;
            
            // Set profile picture if available
            if (profileData.profile_picture_link && profileData.profile_picture_link.trim() !== '') {
              userProfilePicture.value = profileData.profile_picture_link;
            }
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    });
    
    // Handle unlock attempt
    const handleUnlock = async () => {
      try {
        loading.value = true;
        errorMessage.value = '';
        
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          router.push('/authentication/sign-in');
          return;
        }
        
        // Attempt to sign in with the current email and provided password
        const { error } = await supabase.auth.signInWithPassword({
          email: session.user.email as string,
          password: password.value
        });
        
        if (error) {
          errorMessage.value = 'Incorrect password. Please try again.';
          return;
        }
        
        // Success - clear the lock and redirect to previous page
        localStorage.removeItem('previousPath');
        sessionStorage.removeItem('app_locked');
        router.push(redirectPath.value);
        
      } catch (error) {
        console.error('Error during unlock:', error);
        errorMessage.value = 'An error occurred. Please try again.';
      } finally {
        loading.value = false;
      }
    };
    
    // Cleanup event listeners on component unmount
    onBeforeUnmount(() => {
      // Remove event listeners
      window.removeEventListener('popstate', handlePopState);
    });
    
    return {
      userFullName,
      userProfilePicture,
      password,
      passwordVisible,
      loading,
      errorMessage,
      togglePasswordVisibility,
      handleUnlock
    };
  }
});
</script>
